/**
 * Phase 6 (2026-05-25) — first-run tour trigger hook.
 *
 * Decides whether to mount <FirstRunTour /> on this render. Combines:
 *   - Clerk hydration state (DO NOT auto-fire while Clerk is loading —
 *     would briefly show signed-out copy to a signed-in user).
 *   - Signed-in branch: trigger if listMyPortals() returns [] AND the
 *     completion flag is unset.
 *   - Signed-out branch: trigger if showcase mode is visible AND the
 *     completion flag is unset.
 *
 * Completion flag: localStorage `repancy:tour-completed`. Cleared on
 * Clerk sign-out via the existing F19.3 `clearMsalSession` sweep
 * (which drops every `repancy:`-prefixed key). The Start Menu "Take
 * the tour" item clears the flag + remounts the tour manually,
 * bypassing this hook's auto-trigger.
 *
 * Returns `{ shouldShow, mode, markComplete, markDismissed,
 * forceShow }` — the desktop mounts <FirstRunTour /> when
 * `shouldShow` is true, threading the matching `mode` through.
 */
import { useEffect, useRef, useState } from 'react';
import { useUser } from '@clerk/react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { useShowcaseVisible } from '../showcase/useShowcaseVisible';
import type { FirstRunTourMode } from '../../components/tour/FirstRunTour';
import { requestAttention } from '../attention/attention';
import { useCurrentAttention } from '../attention/useAttention';

export const TOUR_COMPLETED_STORAGE_KEY = 'repancy:tour-completed';
export const LEGACY_ORIENTATION_KEY = 'repancy-orientation-completed';
export const FIRST_RUN_TOUR_ATTENTION_ID = 'first-run-tour';

function readCompleted(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(TOUR_COMPLETED_STORAGE_KEY) === 'true';
  } catch {
    // Privacy-mode browsers can throw; if we can't read the flag we
    // skip the tour rather than risking a re-fire on every render.
    return true;
  }
}

function writeCompleted(value: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (value) {
      window.localStorage.setItem(TOUR_COMPLETED_STORAGE_KEY, 'true');
    } else {
      window.localStorage.removeItem(TOUR_COMPLETED_STORAGE_KEY);
    }
  } catch {
    /* localStorage write failure — ignore */
  }
}

/**
 * One-shot migration. The legacy "show me around" flag
 * (`repancy-orientation-completed`) becomes
 * `repancy:tour-completed` on the next app boot. Idempotent.
 */
export function migrateLegacyOrientationFlag(): void {
  if (typeof window === 'undefined') return;
  try {
    const legacy = window.localStorage.getItem(LEGACY_ORIENTATION_KEY);
    if (legacy !== null) {
      if (legacy === 'true' && readCompleted() === false) {
        writeCompleted(true);
      }
      window.localStorage.removeItem(LEGACY_ORIENTATION_KEY);
    }
  } catch {
    /* localStorage unavailable — ignore */
  }
}

export interface UseFirstRunTourResult {
  shouldShow: boolean;
  mode: FirstRunTourMode;
  /** Flip the localStorage flag so the tour stops auto-firing. */
  markComplete: () => void;
  /** Same as markComplete — kept as a separate name for telemetry clarity. */
  markDismissed: () => void;
  /**
   * Manual relaunch. Clears the localStorage flag + forces the tour
   * visible on the next render. Wired to the Start Menu
   * "Take the tour" item.
   */
  forceShow: () => void;
}

/**
 * Pure decision helper, extracted for unit testability. Mirrors the
 * shape of `resolveShowcaseVisibility` in useShowcaseVisible.ts.
 */
export function resolveFirstRunTour(opts: {
  clerkLoaded: boolean;
  clerkUserId: string | null;
  portalsLoaded: boolean;
  portalCount: number;
  showcaseVisible: boolean;
  flagCompleted: boolean;
  forceVisible: boolean;
}): { shouldShow: boolean; mode: FirstRunTourMode } {
  // forceVisible (manual relaunch) wins over every other gate.
  if (opts.forceVisible) {
    const mode: FirstRunTourMode = opts.clerkUserId !== null ? 'signed-in' : 'signed-out-showcase';
    return { shouldShow: true, mode };
  }
  // Don't auto-fire while Clerk is loading — would flash the wrong
  // copy on hard refresh.
  if (!opts.clerkLoaded) return { shouldShow: false, mode: 'signed-in' };
  // Already done — don't auto-fire.
  if (opts.flagCompleted) return { shouldShow: false, mode: 'signed-in' };
  // Signed-in: gate on listMyPortals having loaded and being empty.
  if (opts.clerkUserId !== null) {
    if (!opts.portalsLoaded) return { shouldShow: false, mode: 'signed-in' };
    if (opts.portalCount === 0) return { shouldShow: true, mode: 'signed-in' };
    return { shouldShow: false, mode: 'signed-in' };
  }
  // Signed-out: gate on showcase visibility.
  if (opts.showcaseVisible) return { shouldShow: true, mode: 'signed-out-showcase' };
  return { shouldShow: false, mode: 'signed-out-showcase' };
}

export function useFirstRunTour(): UseFirstRunTourResult {
  const { isLoaded, user } = useUser();
  const clerkUserId = user?.id ?? null;
  const [showcaseVisible] = useShowcaseVisible();
  const myPortals = useQuery(
    api.portalAccess.listMyPortals,
    clerkUserId !== null ? {} : 'skip',
  );
  const [flagCompleted, setFlagCompleted] = useState(readCompleted);
  const [forceVisible, setForceVisible] = useState(false);

  // Run the legacy-flag migration once on mount.
  useEffect(() => {
    migrateLegacyOrientationFlag();
    setFlagCompleted(readCompleted());
  }, []);

  // Decide whether the tour WANTS to fire (independent of attention
  // turn). When yes, request an attention turn from the orchestrator;
  // the actual render gate (`shouldShow`) only flips true when the
  // orchestrator hands us the turn.
  const { shouldShow: wantsToShow, mode } = resolveFirstRunTour({
    clerkLoaded: isLoaded,
    clerkUserId,
    portalsLoaded: myPortals !== undefined,
    portalCount: Array.isArray(myPortals) ? myPortals.length : 0,
    showcaseVisible,
    flagCompleted,
    forceVisible,
  });

  // Resolver for the queued attention turn — set when we request a
  // turn, called from markComplete/markDismissed so the orchestrator
  // can move on to the next queued moment.
  const turnResolverRef = useRef<(() => void) | null>(null);
  // Belt-and-suspenders so we don't enqueue duplicate requests from
  // React's render-driven double-fires; the orchestrator dedupes by
  // id but skipping the enqueue is cleaner.
  const enqueuedRef = useRef(false);

  useEffect(() => {
    if (!wantsToShow) return;
    if (enqueuedRef.current) return;
    enqueuedRef.current = true;

    void requestAttention({
      id: FIRST_RUN_TOUR_ATTENTION_ID,
      priority: 10,
      // User-driven — only resolves when the user clicks Done /
      // Skip. Opts out of the orchestrator's MAX_RUN_MS cap (would
      // otherwise force-resolve at 15s and let signin-welcome /
      // portal-welcome render on top of the still-open tour).
      userDriven: true,
      // Re-check the flag at turn time (not enqueue time). This
      // closes the legacy-migration race: even if the predicate
      // ran before the mount-time migration completed, the second
      // check at turn time sees the migrated state.
      shouldFire: () => {
        migrateLegacyOrientationFlag();
        return readCompleted() === false;
      },
      run: () =>
        new Promise<void>((resolve) => {
          turnResolverRef.current = resolve;
        }),
    });
  }, [wantsToShow]);

  // Actual render gate. True only when the orchestrator has handed
  // us the turn AND the tour still wants to show. This decouples
  // "tour wants to fire" (computed from Clerk + portals + flag)
  // from "tour is allowed on screen now" (orchestrator-owned).
  const current = useCurrentAttention();
  const shouldShow =
    wantsToShow && current === FIRST_RUN_TOUR_ATTENTION_ID;

  function markComplete(): void {
    writeCompleted(true);
    setFlagCompleted(true);
    setForceVisible(false);
    // Release the attention turn so the orchestrator can drain the
    // next queued moment. Null-safe in case markComplete fires
    // before the request reached its turn (e.g. user dismisses
    // an externally-rendered tour).
    if (turnResolverRef.current !== null) {
      turnResolverRef.current();
      turnResolverRef.current = null;
    }
    enqueuedRef.current = false;
  }

  return {
    shouldShow,
    mode,
    markComplete,
    markDismissed: markComplete,
    forceShow: () => {
      writeCompleted(false);
      setFlagCompleted(false);
      setForceVisible(true);
      // Reset the dedupe so the new wantsToShow flip enqueues a
      // fresh turn request.
      enqueuedRef.current = false;
    },
  };
}
